import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { PremiumFeatures } from "@/components/premium/PremiumFeatures";
import { useNavigate, useSearchParams } from "react-router-dom";

export default function Subscription() {
  const [loading, setLoading] = useState(true);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const success = searchParams.get('success');

  useEffect(() => {
    if (success === 'true') {
      toast({
        title: "Subscription successful!",
        description: "Thank you for subscribing to our premium features",
      });
      navigate('/subscription', { replace: true });
    } else if (success === 'false') {
      toast({
        title: "Subscription cancelled",
        description: "No worries! You can subscribe to premium features whenever you're ready.",
        variant: "default",
      });
      navigate('/subscription', { replace: true });
    }
  }, [success, toast, navigate]);

  useEffect(() => {
    checkSubscription();
  }, []);

  const checkSubscription = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        setIsSubscribed(false);
        return;
      }

      const { data, error } = await supabase.functions.invoke('check-subscription');
      if (error) throw error;
      setIsSubscribed(data.subscribed);
    } catch (error) {
      console.error('Error checking subscription:', error);
      toast({
        title: "Error",
        description: "Could not check subscription status",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async () => {
    try {
      const { data, error } = await supabase.functions.invoke('create-checkout');
      
      if (error) throw error;
      
      if (data.error) {
        if (data.subscribed) {
          toast({
            title: "Already subscribed",
            description: "You already have an active subscription",
          });
          setIsSubscribed(true);
          return;
        }
        throw new Error(data.error);
      }

      if (data.url) {
        window.location.href = data.url;
      }
    } catch (error) {
      console.error('Error creating checkout session:', error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Could not start subscription process",
        variant: "destructive",
      });
    }
  };

  if (loading) {
    return (
      <div className="container max-w-2xl py-8">
        <h1 className="text-2xl font-bold mb-6">Subscription</h1>
        <Card className="p-6">
          <div className="text-center">Loading...</div>
        </Card>
      </div>
    );
  }

  return (
    <div className="container max-w-2xl py-8">
      <h1 className="text-2xl font-bold mb-6">Subscription</h1>
      <Card className="p-6 mb-6">
        <div className="text-center mb-4">
          {isSubscribed ? (
            <div className="text-green-600 font-semibold">
              ✓ You have an active subscription
            </div>
          ) : (
            <>
              <p className="text-muted-foreground mb-4">
                Unlock premium features to enhance your DBT journey
              </p>
              <Button onClick={handleSubscribe} className="w-full sm:w-auto">
                Subscribe Now
              </Button>
            </>
          )}
        </div>
      </Card>
      <PremiumFeatures />
    </div>
  );
}